import { useEffect, useState } from 'react'
import { useAppSelector } from '../../hooks/redux'
import { getFinanceAuditLogs, FinanceAuditLogEntry, GetFinanceAuditLogsParams } from '../../services/api/finance'
import { ChevronDownIcon, ChevronRightIcon, ClockIcon } from '@heroicons/react/24/outline'

interface FinanceAuditLogProps {
  /** 指定 section 篩選（contacts/units/parking/bills/reconciliation/settings） */
  section?: GetFinanceAuditLogsParams['section']
  /** 指定單一 entityType */
  entityType?: string
  /** 指定單一 entityId（搭配 entityType 使用） */
  entityId?: string
  /** 每次最多撈幾筆，預設 30 */
  limit?: number
  /** 預設標題文字 */
  title?: string
  /** 是否預設展開，預設 false */
  defaultOpen?: boolean
  /** 緊湊模式（用於 modal 內） */
  compact?: boolean
  /** 外層 className 疊加 */
  className?: string
}

// 依 action 前綴決定顏色
const actionColor = (action: string): string => {
  if (action.startsWith('POST') || action.startsWith('CREATE')) return 'bg-green-100 text-green-800'
  if (action.startsWith('PATCH') || action.startsWith('PUT') || action.startsWith('UPDATE')) return 'bg-blue-100 text-blue-800'
  if (action.startsWith('DELETE') || action.startsWith('REMOVE')) return 'bg-red-100 text-red-800'
  return 'bg-gray-100 text-gray-700'
}

const actionLabel = (action: string): string => {
  // 去掉 HTTP method 前綴、留底層資源名稱
  const map: Record<string, string> = {
    POST: '新增', PATCH: '更新', PUT: '更新', DELETE: '刪除',
  }
  for (const prefix of Object.keys(map)) {
    if (action.startsWith(prefix + '_') || action === prefix) {
      return map[prefix]
    }
  }
  return action
}

const entityLabel = (et: string): string => {
  const labels: Record<string, string> = {
    Contact: '聯絡人',
    Unit: '戶別',
    UnitContact: '住戶關聯',
    UnitFeeAdjustment: '費用調整',
    ParkingSpace: '車位',
    MonthlyBill: '帳單',
    AchExport: 'ACH 匯出',
    BankFileUpload: '銀行檔案',
    UnmatchedRecord: '未匹配紀錄',
    FinanceSettings: '財務設定',
    FeeAdjustmentType: '費用類型',
    Finance: '財務',
  }
  return labels[et] || et
}

const formatTime = (iso: string): string => {
  try {
    const d = new Date(iso)
    return d.toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
  } catch {
    return iso
  }
}

// 欄位中文對照（用於 diff 顯示）
const FIELD_LABELS: Record<string, string> = {
  name: '姓名',
  idNumber: '身分證/統編',
  email: 'Email',
  phone: '電話',
  lineId: 'LINE ID',
  bankCode: '銀行代碼',
  bankAccount: '銀行帳號',
  ownerMeetingPostalCode: '區權人大會郵寄郵遞區號',
  ownerMeetingAddress: '區權人大會郵寄地址',
  note: '備註',
  taxId: '統一編號',
  contactName: '聯絡人',
  contactPhone: '聯絡電話',
  address: '地址',
  tags: '標籤',
  unitCode: '戶號',
  building: '棟別',
  floor: '樓層',
  unitNumber: '門牌附號',
  address: '地址',
  pingArea: '坪數',
  paymentMethod: '繳費方式',
  paymentFrequency: '繳費週期',
  payerId: '繳費人',
  spaceCode: '車位編號',
  type: '類型',
  unitId: '所屬戶別',
  ownerId: '所有人',
  currentUserId: '使用人',
  currentUserLeaseStart: '租期起',
  currentUserLeaseEnd: '租期迄',
  licensePlates: '車牌',
}

// 需忽略的欄位（不納入 diff）
const IGNORE_KEYS = new Set(['id', 'createdAt', 'updatedAt', 'projectId'])

const isPrimitive = (v: unknown): boolean =>
  v === null || v === undefined || ['string', 'number', 'boolean'].includes(typeof v)

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'object') {
    // Unit.payer { name } / ParkingSpace.unit { unitCode } 之類的關聯物件
    const o = v as Record<string, unknown>
    if (typeof o.name === 'string') return o.name
    if (typeof o.unitCode === 'string') return o.unitCode
    if (typeof o.spaceCode === 'string') return o.spaceCode
    if (Array.isArray(v)) return v.map(x => formatValue(x)).join(', ')
    return JSON.stringify(v)
  }
  if (typeof v === 'string' && v.length > 40) return v
  return String(v)
}

const computeDiff = (oldV: any, newV: any): Array<{ key: string; before: unknown; after: unknown }> => {
  const diffs: Array<{ key: string; before: unknown; after: unknown }> = []
  const keys = new Set<string>()
  if (oldV && typeof oldV === 'object') Object.keys(oldV).forEach(k => keys.add(k))
  if (newV && typeof newV === 'object') Object.keys(newV).forEach(k => keys.add(k))
  for (const k of keys) {
    if (IGNORE_KEYS.has(k)) continue
    const a = oldV?.[k]
    const b = newV?.[k]
    // 只比較純值或單層關聯物件 name/unitCode；其他深層物件一律比 JSON string
    const normA = isPrimitive(a) ? a : JSON.stringify(a)
    const normB = isPrimitive(b) ? b : JSON.stringify(b)
    if (normA === normB) continue
    diffs.push({ key: k, before: a, after: b })
  }
  return diffs
}

const AuditLogItem = ({ log, compact }: { log: FinanceAuditLogEntry; compact: boolean }) => {
  const [expanded, setExpanded] = useState(false)
  const hasDiff = !!(log.oldValues || log.newValues)
  const diffs = hasDiff ? computeDiff(log.oldValues, log.newValues) : []
  const isCreate = log.action.startsWith('POST')
  const isDelete = log.action.startsWith('DELETE')

  return (
    <li className={compact ? 'px-3 py-1.5' : 'px-4 py-2'}>
      <button
        type="button"
        onClick={() => hasDiff && setExpanded(v => !v)}
        className={`w-full text-left ${hasDiff ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="text-gray-500 whitespace-nowrap">{formatTime(log.createdAt)}</span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${actionColor(log.action)}`}>
            {actionLabel(log.action)} · {entityLabel(log.entityType)}
          </span>
          <span className="text-gray-700 truncate" title={log.userEmail}>{log.userEmail}</span>
        </div>
        {log.description && (
          <div className="text-xs text-gray-600 mt-0.5 font-medium truncate" title={log.description}>
            {log.description}
          </div>
        )}
      </button>

      {expanded && hasDiff && (
        <div className="mt-2 text-xs border-t border-gray-100 pt-2">
          {isCreate ? (
            <div>
              <div className="text-gray-500 mb-1">新增內容：</div>
              <table className="w-full">
                <tbody>
                  {Object.keys(log.newValues || {})
                    .filter(k => !IGNORE_KEYS.has(k))
                    .map(k => {
                      const v = (log.newValues as any)?.[k]
                      if (v === null || v === undefined || v === '') return null
                      return (
                        <tr key={k}>
                          <td className="text-gray-500 pr-2 py-0.5 align-top whitespace-nowrap">
                            {FIELD_LABELS[k] || k}
                          </td>
                          <td className="py-0.5 text-gray-900 break-all">{formatValue(v)}</td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </div>
          ) : isDelete ? (
            <div>
              <div className="text-gray-500 mb-1">刪除前內容：</div>
              <table className="w-full">
                <tbody>
                  {Object.keys(log.oldValues || {})
                    .filter(k => !IGNORE_KEYS.has(k))
                    .map(k => {
                      const v = (log.oldValues as any)?.[k]
                      if (v === null || v === undefined || v === '') return null
                      return (
                        <tr key={k}>
                          <td className="text-gray-500 pr-2 py-0.5 align-top whitespace-nowrap">
                            {FIELD_LABELS[k] || k}
                          </td>
                          <td className="py-0.5 text-gray-900 break-all">{formatValue(v)}</td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </div>
          ) : diffs.length === 0 ? (
            <div className="text-gray-400">（無可比對的欄位變更）</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-gray-400 text-[11px]">
                  <th className="text-left pr-2 font-normal">欄位</th>
                  <th className="text-left pr-2 font-normal">變更前</th>
                  <th className="text-left font-normal">變更後</th>
                </tr>
              </thead>
              <tbody>
                {diffs.map(d => (
                  <tr key={d.key}>
                    <td className="text-gray-500 pr-2 py-0.5 align-top whitespace-nowrap">
                      {FIELD_LABELS[d.key] || d.key}
                    </td>
                    <td className="text-red-700 pr-2 py-0.5 break-all line-through">
                      {formatValue(d.before)}
                    </td>
                    <td className="text-green-700 py-0.5 break-all">{formatValue(d.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </li>
  )
}

export const FinanceAuditLog = ({
  section,
  entityType,
  entityId,
  limit = 30,
  title,
  defaultOpen = false,
  compact = false,
  className = '',
}: FinanceAuditLogProps) => {
  const { currentProject } = useAppSelector(s => s.project)
  const [open, setOpen] = useState(defaultOpen)
  const [logs, setLogs] = useState<FinanceAuditLogEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const defaultTitle = title ?? (entityId ? '編輯紀錄' : '操作紀錄')

  const load = async () => {
    if (!currentProject?.id) return
    setLoading(true)
    setError(null)
    try {
      const res = await getFinanceAuditLogs(currentProject.id, {
        section,
        entityType,
        entityId,
        limit,
      })
      if (res.success && res.data) {
        setLogs(res.data.logs)
      }
      setLoaded(true)
    } catch (e: any) {
      setError(e?.message || '載入失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && !loaded && currentProject?.id) {
      load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentProject?.id])

  // 外部 prop 改變時重新載入（例如從不同 entity 開啟）
  useEffect(() => {
    if (loaded) {
      setLoaded(false)
      setLogs([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, entityType, entityId])

  return (
    <div className={`bg-white rounded-md border border-gray-200 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between ${compact ? 'px-3 py-2' : 'px-4 py-3'} text-sm font-medium text-gray-700 hover:bg-gray-50`}
      >
        <div className="flex items-center space-x-2">
          <ClockIcon className="w-4 h-4 text-gray-500" />
          <span>{defaultTitle}</span>
          {loaded && <span className="text-xs text-gray-500">（{logs.length} 筆）</span>}
        </div>
        {open ? <ChevronDownIcon className="w-4 h-4 text-gray-400" /> : <ChevronRightIcon className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="border-t border-gray-200">
          {loading ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
            </div>
          ) : error ? (
            <div className="p-3 text-sm text-red-600">載入失敗：{error}</div>
          ) : logs.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 text-center">暫無紀錄</div>
          ) : (
            <ul className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {logs.map(log => (
                <AuditLogItem key={log.id} log={log} compact={compact} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default FinanceAuditLog
